import { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../ui/Breadcrumb';
import ModuleIcon from '../ui/ModuleIcon';
import { IconChevronRight, IconKeyboard } from '../ui/icons';
import { moduleColor, orderModules } from '../ui/modulePalette';
import { SettingItem, SettingsSectionTitle, Toggle } from '../ui/SettingsControls';

const ADMIN_ONLY_KEYS = new Set(['clients', 'sites', 'logs', 'admin_settings']);

const DEFAULT_SETTINGS = {
  appearance: {
    theme_mode: 'dark',
    font: 'space',
    background_preset_dark: 'midnight',
    background_preset_light: 'lavender',
    mobile_header_sticky: false,
  },
  modules: {
    preload_images: false,
    resources_default_view: 'folder_file',
    resources_default_filter: 'none',
    resources_default_sort: 'none',
  },
  traffic: {
    default_range: '7d',
  },
  projects: {
    default_view: 'editor',
    auto_expand_all_sections: false,
    section_open_mode: 'multi',
    container_open_mode: 'multi',
    show_image_preview: true,
    draft_mode: true,
    show_contrast_indicator: true,
    show_dynamic_tokens: true,
    shortcuts: { format_biu: true, save: true, publish: true },
  },
};

function safeSettings(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_SETTINGS;
  return {
    appearance: { ...DEFAULT_SETTINGS.appearance, ...(raw.appearance || {}) },
    modules: { ...DEFAULT_SETTINGS.modules, ...(raw.modules || {}) },
    traffic: { ...DEFAULT_SETTINGS.traffic, ...(raw.traffic || {}) },
    projects: {
      ...DEFAULT_SETTINGS.projects,
      ...(raw.projects || {}),
      shortcuts: { ...DEFAULT_SETTINGS.projects.shortcuts, ...((raw.projects || {}).shortcuts || {}) },
    },
  };
}

function ModuleNavButton({ label, iconKey, accent, right, disabled, onClick }) {
  return (
    <button type="button" className="ListItem ListItem--button ModuleNavButton" style={{ '--accent': accent }} onClick={disabled ? undefined : onClick} disabled={disabled}>
      <span className="ModuleNavLeft">
        <span className="ModuleNavIcon" aria-hidden="true">
          <ModuleIcon moduleKey={iconKey} />
        </span>
        <span className="ModuleNavText">{label}</span>
      </span>
      <span className="ModuleNavRight">
        {right ? right : null}
        <IconChevronRight className="ModuleNavChevron" aria-hidden="true" />
      </span>
    </button>
  );
}

export default function SettingsPage({ siteId, modules, routeRest, onNavigate, user, simulation, panelSettings, onSaveSettings }) {
  const rest = Array.isArray(routeRest) ? routeRest : [];
  const subRoute = String(rest[0] || '');

  const canEdit = Boolean(panelSettings?.editable);
  const loading = panelSettings?.status === 'loading';
  const loadedSettings = safeSettings(panelSettings?.settings);
  const saveError = String(panelSettings?.error || '').trim();

  const siteModules = useMemo(() => {
    const list = (modules || [])
      .filter((m) => m && m.key && m.route)
      .filter((m) => m.key !== 'dashboard')
      .filter((m) => m.key !== 'settings')
      .filter((m) => !ADMIN_ONLY_KEYS.has(String(m.key)));
    return orderModules(list);
  }, [modules]);

  const selectedModule = useMemo(() => {
    if (!subRoute) return null;
    return siteModules.find((m) => String(m.key) === subRoute) || null;
  }, [siteModules, subRoute]);

  const breadcrumb = useMemo(() => {
    if (!subRoute) return [{ label: 'Paramètres du site' }];
    if (selectedModule) return [{ label: 'Paramètres du site', onClick: () => onNavigate?.('/settings') }, { label: selectedModule.label }];
    return [{ label: 'Paramètres du site', onClick: () => onNavigate?.('/settings') }, { label: subRoute }];
  }, [subRoute, selectedModule, onNavigate]);

  const [localSaving, setLocalSaving] = useState(false);

  useEffect(() => {
    if (subRoute !== 'appearance') return;
    onNavigate?.('/account');
  }, [subRoute, onNavigate]);

  async function save(next, { forceReconnect = false } = {}) {
    if (!onSaveSettings) return;
    if (!canEdit) return;
    setLocalSaving(true);
    try {
      await onSaveSettings(next, { forceReconnect });
    } finally {
      setLocalSaving(false);
    }
  }

  function patchProjects(patch) {
    const next = { ...loadedSettings, projects: { ...loadedSettings.projects, ...(patch || {}) } };
    save(next);
  }

  function patchModules(patch) {
    const next = { ...loadedSettings, modules: { ...loadedSettings.modules, ...(patch || {}) } };
    save(next);
  }

  function patchTraffic(patch) {
    const next = { ...loadedSettings, traffic: { ...loadedSettings.traffic, ...(patch || {}) } };
    save(next);
  }

  function patchProjectShortcuts(patch) {
    const next = {
      ...loadedSettings,
      projects: {
        ...loadedSettings.projects,
        shortcuts: { ...(loadedSettings.projects.shortcuts || {}), ...(patch || {}) },
      },
    };
    save(next);
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={breadcrumb} />
        </div>
      </header>

      {saveError ? (
        <div className="Error" role="alert" style={{ marginBottom: 12 }}>
          {saveError}
        </div>
      ) : null}

      {loading ? (
        <div className="Card" role="status" aria-live="polite" style={{ marginBottom: 12 }}>
          Chargement…
        </div>
      ) : null}

      {!subRoute ? (
        <div style={{ display: 'grid', gap: 14 }}>
          <SettingsSectionTitle style={{ marginTop: 8 }}>Modules</SettingsSectionTitle>

          <div className="List">
            {siteModules.map((m) => (
              <ModuleNavButton
                key={m.key}
                label={m.label}
                iconKey={m.key}
                accent={moduleColor(m.key, m.color)}
                right={m.coming_soon ? <span className="Badge">à venir</span> : null}
                disabled={m.coming_soon}
                onClick={() => onNavigate?.(`/settings/${String(m.key)}`)}
              />
            ))}
          </div>

          <div className="Card" style={{ padding: 12 }}>
            <SettingItem
              label="Pré-charger les images"
              right={
                <Toggle
                  checked={Boolean(loadedSettings.modules.preload_images)}
                  onChange={(v) => patchModules({ preload_images: v })}
                  disabled={!canEdit || localSaving}
                  ariaLabel="Pré-charger les images"
                />
              }
            >
              Charge plus agressivement les images des modules visuels (Ressources, Galerie).
            </SettingItem>
          </div>
        </div>
      ) : null}

      {subRoute === 'account' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <SettingsSectionTitle>Compte</SettingsSectionTitle>
          <SettingItem
            label="Email"
            right={<span className="Muted">{String(user?.email || '—')}</span>}
          />
          <SettingItem
            label="Nom"
            right={
              <span className="Muted">{String(user?.display_name || '').trim() || '—'}</span>
            }
          />
        </div>
      ) : null}

      {selectedModule ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {selectedModule.key !== 'projects' ? <SettingsSectionTitle>{selectedModule.label}</SettingsSectionTitle> : null}

          {selectedModule.key === 'projects' ? (
            <>
              <SettingItem
                label="Vue par défaut"
                right={
                  <select
                    className="Select"
                    value={String(loadedSettings.projects.default_view || 'editor')}
                    onChange={(e) => patchProjects({ default_view: e.target.value })}
                    disabled={!canEdit || localSaving}
                  >
                    <option value="info">Infos projet</option>
                    <option value="editor">Éditeur de sections</option>
                  </select>
                }
              >
                Quand tu ouvres un projet, quelle vue afficher en premier.
              </SettingItem>

              <SettingItem
                label="Ouvrir toutes les sections à l’arrivée"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.auto_expand_all_sections)}
                    onChange={(v) => patchProjects({ auto_expand_all_sections: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Ouvrir toutes les sections à l’arrivée"
                  />
                }
              >
                Dans l’éditeur, déplie automatiquement toutes les sections.
              </SettingItem>

              <SettingItem
                label="Ouverture des sections"
                right={
                  <select
                    className="Select"
                    value={String(loadedSettings.projects.section_open_mode || 'multi')}
                    onChange={(e) => patchProjects({ section_open_mode: e.target.value })}
                    disabled={!canEdit || localSaving}
                  >
                    <option value="multi">Plusieurs ouvertes</option>
                    <option value="single">Une seule à la fois</option>
                  </select>
                }
              >
                “Une seule” replie les autres sections quand tu en ouvres une.
              </SettingItem>

              <SettingItem
                label="Ouverture des conteneurs"
                right={
                  <select
                    className="Select"
                    value={String(loadedSettings.projects.container_open_mode || 'multi')}
                    onChange={(e) => patchProjects({ container_open_mode: e.target.value })}
                    disabled={!canEdit || localSaving}
                  >
                    <option value="multi">Plusieurs ouverts</option>
                    <option value="single">Un seul à la fois</option>
                  </select>
                }
              >
                Même principe que les sections, mais pour les conteneurs.
              </SettingItem>

              <SettingItem
                label="Aperçu des images"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.show_image_preview)}
                    onChange={(v) => patchProjects({ show_image_preview: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Aperçu des images"
                  />
                }
              >
                Affiche un aperçu quand un lien vers une image est utilisé.
              </SettingItem>

              <SettingItem
                label="Mode brouillon"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.draft_mode)}
                    onChange={(v) => patchProjects({ draft_mode: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Mode brouillon"
                  />
                }
              >
                Si activé: Cmd/Ctrl+S enregistre en brouillon et Cmd/Ctrl+Shift+S publie.
              </SettingItem>

              <SettingItem
                label="Indicateur de contraste"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.show_contrast_indicator)}
                    onChange={(v) => patchProjects({ show_contrast_indicator: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Indicateur de contraste"
                  />
                }
              >
                Affiche un indicateur pour vérifier la lisibilité (WCAG).
              </SettingItem>

              <SettingItem
                label="Boutons de valeurs dynamiques"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.show_dynamic_tokens)}
                    onChange={(v) => patchProjects({ show_dynamic_tokens: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Boutons de valeurs dynamiques"
                  />
                }
              >
                Affiche les boutons d’insertion des tokens (ex: <code>{'{{section_number}}'}</code>).
              </SettingItem>

              <SettingsSectionTitle style={{ marginTop: 6, fontSize: 16 }}>
                <span className="ButtonInline" style={{ gap: 10 }}>
                  <IconKeyboard /> Raccourcis clavier
                </span>
              </SettingsSectionTitle>

              <SettingItem
                label="Ctrl/Cmd+B, I, U dans les textarea"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.shortcuts.format_biu)}
                    onChange={(v) => patchProjectShortcuts({ format_biu: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Raccourcis de formatage Ctrl/Cmd+B, I, U"
                  />
                }
              >
                Active le formatage rapide dans les éditeurs de texte.
              </SettingItem>

              <SettingItem
                label="Ctrl/Cmd+S (sauvegarder)"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.shortcuts.save)}
                    onChange={(v) => patchProjectShortcuts({ save: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Raccourci sauvegarde Ctrl/Cmd+S"
                  />
                }
              >
                Enregistre sans passer par les boutons.
              </SettingItem>

              <SettingItem
                label="Ctrl/Cmd+Shift+S (publier)"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.projects.shortcuts.publish)}
                    onChange={(v) => patchProjectShortcuts({ publish: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Raccourci publication Ctrl/Cmd+Shift+S"
                  />
                }
              >
                Ouvre la confirmation puis publie le brouillon.
              </SettingItem>
            </>
          ) : selectedModule.key === 'traffic' ? (
            <SettingItem
              label="Vue de période par défaut"
              right={
                <select
                  className="Select"
                  value={String(loadedSettings.traffic.default_range || '7d')}
                  onChange={(e) => patchTraffic({ default_range: e.target.value })}
                  disabled={!canEdit || localSaving}
                >
                  <option value="24h">24 heures</option>
                  <option value="7d">7 jours</option>
                  <option value="30d">30 jours</option>
                </select>
              }
            >
              Période affichée par défaut dans le module Trafic Web.
            </SettingItem>
          ) : selectedModule.key === 'resources' ? (
            <>
              <SettingItem
                label="Vue par défaut"
                right={
                  <select
                    className="Select"
                    value={String(loadedSettings.modules.resources_default_view || 'folder_file')}
                    onChange={(e) => patchModules({ resources_default_view: e.target.value })}
                    disabled={!canEdit || localSaving}
                  >
                    <option value="folder_file">Dossier puis fichier</option>
                    <option value="mixed">Dossier et fichier mélangés</option>
                  </select>
                }
              >
                Définit l’ordre d’affichage au chargement du module Ressources.
              </SettingItem>

              <SettingItem
                label="Filtre par défaut"
                right={
                  <select
                    className="Select"
                    value={String(loadedSettings.modules.resources_default_filter || 'none')}
                    onChange={(e) => patchModules({ resources_default_filter: e.target.value })}
                    disabled={!canEdit || localSaving}
                  >
                    <option value="none">Aucun</option>
                    <option value="folder">Dossiers</option>
                    <option value="file">Fichiers</option>
                    <option value="image">Images</option>
                    <option value="video">Vidéos</option>
                    <option value="audio">Audio</option>
                    <option value="document">Documents</option>
                  </select>
                }
              >
                Filtre pré-sélectionné à l’ouverture de la page.
              </SettingItem>

              <SettingItem
                label="Tri par défaut"
                right={
                  <select
                    className="Select"
                    value={String(loadedSettings.modules.resources_default_sort || 'none')}
                    onChange={(e) => patchModules({ resources_default_sort: e.target.value })}
                    disabled={!canEdit || localSaving}
                  >
                    <option value="none">Aucun</option>
                    <option value="name_asc">Nom (A → Z)</option>
                    <option value="name_desc">Nom (Z → A)</option>
                    <option value="size_desc">Poids (plus lourd)</option>
                    <option value="size_asc">Poids (plus léger)</option>
                    <option value="dimension_desc">Dimensions (plus grand)</option>
                    <option value="dimension_asc">Dimensions (plus petit)</option>
                  </select>
                }
              >
                Tri appliqué automatiquement au chargement.
              </SettingItem>

              <SettingItem
                label="Pré-charger les images"
                right={
                  <Toggle
                    checked={Boolean(loadedSettings.modules.preload_images)}
                    onChange={(v) => patchModules({ preload_images: v })}
                    disabled={!canEdit || localSaving}
                    ariaLabel="Pré-charger les images"
                  />
                }
              >
                Réduit l’attente d’affichage des miniatures, au prix d’un plus gros chargement initial.
              </SettingItem>
            </>
          ) : (
            <div className="Muted">Aucun paramètre pour ce module pour le moment.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
